import { Link } from '@remix-run/react';
import cx from 'classnames';

import { Image } from '~/components/Image';
import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import PostCardv3 from '~/components/PostCardv3';
import Typography from '~/components/Typography';

import type { LandingPage } from '~/interfaces/landing_page';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import type { LoaderDataProps } from '~/routes/subscribe';

interface Props {
  landingPage: LandingPage;
  featuredPosts: any[];
}

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
const PostLink = ({ post, screenSize, fontFamily, headlineColor }: any) => {
  return (
    <Link to={`/p/${post.slug}`}>
      <div className="w-full space-y-1">
        <Typography
          as="h4"
          size="md"
          weight="semibold"
          fontFamily={fontFamily}
          color={headlineColor}
          className={cx({
            'w-full': true,
          })}
        >
          {post?.web_title}
        </Typography>
        <Typography
          as="p"
          size="xs"
          weight="regular"
          fontFamily={fontFamily}
          color={headlineColor}
          className={cx({
            'w-full opacity-70': true,
          })}
        >
          {post?.web_subtitle}
        </Typography>
      </div>
    </Link>
  );
};

const MainCard = ({ post, fontFamily, headlineColor }: any) => {
  return (
    <Link to={`/p/${post.slug}`} prefetch="none" className="w-full max-w-xl rounded bg-transparent shadow-md">
      <div className="z-10 col-span-1 mb-2 w-full overflow-hidden rounded-wt">
        <Image src={post.image_url} alt={post.web_title} maxWidth={800} loading="lazy" aspectRatio="social" />
      </div>
      <div className="w-full space-y-1 p-4">
        <Typography
          as="h4"
          size="xl"
          weight="semibold"
          fontFamily={fontFamily}
          color={headlineColor}
          className={cx({
            'w-full': true,
          })}
        >
          {post?.web_title}
        </Typography>
        <Typography
          as="p"
          size="md"
          weight="regular"
          fontFamily={fontFamily}
          color={headlineColor}
          className={cx({
            'w-full opacity-70': true,
          })}
        >
          {post?.web_subtitle}
        </Typography>
      </div>
    </Link>
  );
};

const ReadMoreCta = ({ text, fontFamily, fontSize, overlineColor }: any) => {
  return (
    <Link to="/" className="mt-4 flex items-center justify-between space-x-2 hover:underline">
      <Typography
        as="span"
        size={fontSize}
        weight="semibold"
        className=""
        fontFamily={fontFamily}
        color={overlineColor}
      >
        {text}
      </Typography>
    </Link>
  );
};

const FeaturedPostsSection = ({ landingPage, featuredPosts }: Props) => {
  const { cardStyles } = useTypedLoaderData<LoaderDataProps>();
  const { isEnabled: hasWebThemesEnabled } = useWebThemeContext();
  const { featured_posts_section } = landingPage;
  const fontFamily = featured_posts_section.headline_font_family;
  const headlineColor = featured_posts_section.headline_color;

  const renderPostsWithAllImages = () => {
    return featuredPosts.length > 0 ? (
      <>
        <div
          className={cx({
            'mx-auto mt-16 grid w-full gap-6 sm:grid-cols-2 md:grid-cols-3': true,
            'justify-center': featured_posts_section.text_align === 'center',
            'justify-start': featured_posts_section.text_align === 'left',
            'justify-end': featured_posts_section.text_align === 'right',
          })}
        >
          {featuredPosts.map((post: any) => {
            return hasWebThemesEnabled ? (
              <PostCardv3 key={post.id} post={post} cardStyles={cardStyles} />
            ) : (
              <MainCard key={post.id} post={post} fontFamily={fontFamily} color={headlineColor} />
            );
          })}
        </div>

        <div
          className={cx({
            'mx-auto mt-8 flex w-full flex-col gap-8 sm:flex-row': true,
            'justify-center': featured_posts_section.text_align === 'center',
            'justify-start': featured_posts_section.text_align === 'left',
            'justify-end': featured_posts_section.text_align === 'right',
          })}
        >
          <ReadMoreCta
            text={featured_posts_section.read_more_cta}
            size={featured_posts_section.read_more_cta_font_size}
            fontFamily={featured_posts_section.read_more_cta_font_family}
            overlineColor={featured_posts_section.overline_color}
          />
        </div>
      </>
    ) : null;
  };

  const renderFirstPostsWithImageOnly = () => {
    return featuredPosts.length > 0 ? (
      <>
        <div
          className={cx({
            'mx-auto mt-16 flex w-full flex-col gap-8 sm:flex-row': true,
            'justify-center': featured_posts_section.text_align === 'center',
            'justify-start': featured_posts_section.text_align === 'left',
            'justify-end': featured_posts_section.text_align === 'right',
          })}
        >
          {hasWebThemesEnabled ? (
            <div className="w-full max-w-2xl">
              <PostCardv3 post={featuredPosts[0]} cardStyles={cardStyles} />
            </div>
          ) : (
            <MainCard post={featuredPosts[0]} fontFamily={fontFamily} color={headlineColor} />
          )}

          {featuredPosts.length !== 1 && (
            <div className="flex w-3/5 flex-col justify-between">
              <div className="flex flex-col space-y-4">
                {featuredPosts?.slice(1).map((post: any) => {
                  return (
                    <div key={post.id}>
                      <PostLink post={post} fontFamily={fontFamily} headlineColor={headlineColor} />
                    </div>
                  );
                })}
              </div>
              <ReadMoreCta
                text={featured_posts_section.read_more_cta}
                size={featured_posts_section.read_more_cta_font_size}
                fontFamily={featured_posts_section.read_more_cta_font_family}
                overlineColor={featured_posts_section.overline_color}
              />
            </div>
          )}
        </div>
        {featuredPosts.length === 1 && (
          <div
            className={cx({
              'mx-auto mt-6 flex w-full': true,
              'justify-center': featured_posts_section.text_align === 'center',
              'justify-start': featured_posts_section.text_align === 'left',
              'justify-end': featured_posts_section.text_align === 'right',
            })}
          >
            <ReadMoreCta
              text={featured_posts_section.read_more_cta}
              size={featured_posts_section.read_more_cta_font_size}
              fontFamily={featured_posts_section.read_more_cta_font_family}
            />
          </div>
        )}
      </>
    ) : null;
  };

  return (
    <div
      style={{
        backgroundColor: featured_posts_section.bg_color,
      }}
      className="flex flex-col p-16 px-4 pb-24"
    >
      <div className="mx-auto w-full max-w-5xl">
        <div className="mb-2 flex w-full flex-col items-center space-y-2">
          {featured_posts_section.overline && (
            <Typography
              as="span"
              size={featured_posts_section.overline_font_size}
              weight="bold"
              fontFamily={featured_posts_section.overline_font_family}
              color={featured_posts_section.overline_color}
              className={cx({
                'w-full': true,
                'text-center': featured_posts_section.text_align === 'center',
                'text-left': featured_posts_section.text_align === 'left',
                'text-right': featured_posts_section.text_align === 'right',
              })}
            >
              {featured_posts_section.overline}
            </Typography>
          )}
          <Typography
            as="h4"
            size={featured_posts_section.headline_font_size}
            weight="bold"
            fontFamily={featured_posts_section.headline_font_family}
            color={featured_posts_section.headline_color}
            className={cx({
              'w-full': true,
              'text-center': featured_posts_section.text_align === 'center',
              'text-left': featured_posts_section.text_align === 'left',
              'text-right': featured_posts_section.text_align === 'right',
            })}
          >
            {featured_posts_section.headline}
          </Typography>
        </div>

        {featured_posts_section.enable_all_post_images ? renderPostsWithAllImages() : renderFirstPostsWithImageOnly()}
      </div>
    </div>
  );
};

export default FeaturedPostsSection;
